import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/demo/lib/Reveal";
import { IMG } from "@/components/demo/lib/images";

const WA = "41765008176";

const SERIES = [
  { id: "01", t: "Aurora Verticale", y: 2026, loc: "Furka Pass", img: IMG.photo.s1 },
  { id: "02", t: "Silence Editions", y: 2025, loc: "Engadina", img: IMG.photo.s2 },
  { id: "03", t: "Capsule — Atelier", y: 2025, loc: "Lugano", img: IMG.photo.s3 },
  { id: "04", t: "Matrimonio S+M", y: 2024, loc: "Lago Maggiore", img: IMG.photo.s4 },
  { id: "05", t: "Tessuti & Pelle", y: 2024, loc: "Milano", img: IMG.photo.s5 },
  { id: "06", t: "Ritratti Olivetti", y: 2023, loc: "Ivrea", img: IMG.photo.s6 },
];

const EDITORIAL = [IMG.photo.e1, IMG.photo.e2, IMG.photo.e3, IMG.photo.e4, IMG.photo.e5, IMG.photo.e6, IMG.photo.e7, IMG.photo.e8];

function Lumiere() {
  const cursorRef = useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (cursorRef.current) cursorRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  return (
    <div className="min-h-screen bg-white text-stone-900 antialiased overflow-x-hidden">
      <style>{`.ff-serif { font-family: 'Cormorant Garamond', 'Times New Roman', serif } body { font-family: 'Inter', sans-serif }`}</style>

      <div ref={cursorRef} className="pointer-events-none fixed top-0 left-0 z-[60] hidden lg:block">
        <div className="h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-stone-900 mix-blend-difference" />
      </div>

      <header className="fixed top-0 inset-x-0 z-50 px-6 sm:px-10 py-6 mix-blend-difference text-white">
        <div className="flex items-center justify-between">
          <div className="ff-serif text-xl tracking-wide">studio <em>lumière</em></div>
          <nav className="hidden md:flex gap-8 text-[10px] tracking-[0.35em] uppercase">
            <a href="#work">Portfolio</a><a href="#editorial">Editorial</a><a href="#about">Studio</a><a href="#book">Contatti</a>
          </nav>
          <div className="text-[10px] tracking-[0.35em] uppercase">Lugano · CH</div>
        </div>
      </header>

      {/* HERO */}
      <section className="min-h-screen flex flex-col justify-end px-6 sm:px-10 pb-16 pt-32 relative">
        <div className="absolute inset-0">
          <img src={IMG.photo.hero} alt="Ritratto editoriale firma di Studio Lumière" className="w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent" />
        </div>
        <div className="relative z-10 text-white">
          <div className="text-[10px] tracking-[0.4em] uppercase mb-8 opacity-80">— Fotografia · Direzione · Stampa fine art</div>
          <h1 className="ff-serif text-[14vw] sm:text-[12vw] lg:text-[9vw] leading-[0.85] tracking-tight break-words">la luce, <em>raccontata.</em></h1>
        </div>
      </section>

      {/* CAPTION ROW */}
      <section className="px-6 sm:px-10 py-12 border-b border-stone-200">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_2fr] gap-12">
          <div className="text-[10px] tracking-[0.4em] uppercase text-stone-500">Progetti selezionati — 2023 / 2026</div>
          <p className="ff-serif text-2xl lg:text-4xl leading-snug max-w-3xl">
            Sei serie. Sei storie. <em className="text-stone-400">Fotografia editoriale e ritrattistica d'autore per brand di lusso, riviste internazionali e clienti privati.</em>
          </p>
        </div>
      </section>

      {/* HORIZONTAL SCROLL */}
      <section id="work" className="py-16">
        <div className="px-6 sm:px-10 mb-10 max-w-7xl mx-auto flex items-end justify-between">
          <div className="text-[10px] tracking-[0.4em] uppercase text-stone-500">→ Scorri</div>
          <div className="ff-serif text-2xl"><em>Portfolio</em></div>
        </div>
        <div className="overflow-x-auto pl-6 sm:pl-10 pb-8">
          <div className="flex gap-6" style={{ width: "max-content" }}>
            {SERIES.map((s, i) => (
              <button key={s.id} onClick={() => setLightbox(i)} className="group w-[78vw] sm:w-[44vw] lg:w-[32vw] flex-shrink-0">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={s.img} alt={`Serie ${s.t}, ${s.loc}`} className="w-full h-full object-cover grayscale hover:grayscale-0 transition duration-1000 group-hover:scale-105" />
                </div>
                <div className="mt-4 flex justify-between items-baseline">
                  <div>
                    <div className="text-[10px] tracking-[0.4em] uppercase text-stone-500">{s.id} — {s.y}</div>
                    <div className="ff-serif text-2xl mt-1">{s.t}</div>
                  </div>
                  <div className="text-xs text-stone-400">{s.loc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL / MASONRY */}
      <section id="editorial" className="px-6 sm:px-10 py-24 bg-stone-50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex justify-between items-end mb-10">
              <h2 className="ff-serif text-5xl lg:text-7xl"><em>Editorial</em></h2>
              <div className="text-[10px] tracking-[0.4em] uppercase text-stone-500">Vogue Italia · Apartamento · Magnum WPP</div>
            </div>
          </Reveal>
          <div className="columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
            {EDITORIAL.map((src, i) => (
              <div key={i} className="break-inside-avoid mb-4 overflow-hidden">
                <img src={src} alt={`Scatto editoriale ${i + 1}`} className={`w-full ${i % 3 === 0 ? "aspect-[3/4]" : i % 2 === 0 ? "aspect-[4/5]" : "aspect-square"} object-cover hover:scale-[1.02] transition duration-700`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="px-6 sm:px-10 py-32">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="ff-serif text-3xl lg:text-5xl leading-snug">
              Sono <em>Aurora Lumière.</em> Fotografo da quindici anni — luce naturale, pellicola medio formato Hasselblad, post-produzione minimale. Lavoro per chi cerca un'immagine che <em>resti nel tempo.</em>
            </div>
            <div className="mt-12 grid sm:grid-cols-3 gap-10 text-sm">
              <div><div className="text-[10px] tracking-[0.4em] uppercase text-stone-500 mb-2">Clienti</div>Hermès · Bulgari · Vacheron Constantin · Aesop · Loro Piana</div>
              <div><div className="text-[10px] tracking-[0.4em] uppercase text-stone-500 mb-2">Pubblicazioni</div>Vogue Italia · Apartamento · Cabana · Kinfolk</div>
              <div><div className="text-[10px] tracking-[0.4em] uppercase text-stone-500 mb-2">Mostre</div>Triennale Milano 2024 · Camera Torino 2023 · m.a.x. museo Chiasso 2022</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BOOK */}
      <section id="book" className="px-6 sm:px-10 py-32 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="ff-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] break-words"><em>Richiedi<br />disponibilità.</em></h2>
          <p className="mt-8 text-stone-300 max-w-md mx-auto">Commission editoriali, ritratti privati e progetti d'autore. Pianificazione 4–8 settimane in anticipo.</p>

          <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Ciao Aurora, vorrei discutere un progetto fotografico.")}`} className="mt-12 inline-block bg-white text-stone-900 px-10 py-5 text-[10px] tracking-[0.4em] uppercase font-semibold hover:bg-stone-200 transition">Scrivimi su WhatsApp</a>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-10 text-[10px] tracking-[0.3em] uppercase text-stone-400 bg-stone-900 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between gap-4">
          <div>© Studio Lumière 2026 · Tutte le immagini sono protette da copyright</div>
          <div>Demo FlashWeb · <Link to="/" className="underline text-white">flash-web.ch</Link></div>
        </div>
      </footer>

      {/* LIGHTBOX */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-[80] bg-black/95 grid place-items-center p-6" onClick={() => setLightbox(null)}>
          <div className="relative max-w-5xl w-full">
            <img src={SERIES[lightbox].img} alt={SERIES[lightbox].t} className="w-full max-h-[85vh] object-contain" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="text-[10px] tracking-[0.4em] uppercase opacity-70">{SERIES[lightbox].id} · {SERIES[lightbox].y} · {SERIES[lightbox].loc}</div>
              <div className="ff-serif text-3xl mt-2"><em>{SERIES[lightbox].t}</em></div>
            </div>
            <button onClick={() => setLightbox(null)} className="absolute top-0 right-0 text-white/70 hover:text-white text-3xl p-3">×</button>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/demo/studio-lumiere")({
  head: () => ({
    meta: [
      { title: "Studio Lumière — Fotografia d'autore a Lugano (Demo FlashWeb)" },
      { name: "description", content: "Fotografia editoriale, ritrattistica d'autore e progetti selezionati per brand di lusso. Richiedi disponibilità per la tua commission." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: Lumiere,
});
